/**
 * Stripe services for handling checkout and billing operations
 */
import { createCheckoutUrl, createPortalUrl } from './plans'
import { loadScript } from '@/lib/utils/scriptLoader'

/**
 * Load the Stripe Pricing Table script
 * @returns {Promise<void>}
 */
export async function loadStripePricingTable() {
  try {
    await loadScript('https://js.stripe.com/v3/pricing-table.js', {
      async: true,
    })
    return true
  }
  catch (error) {
    console.error('Failed to load Stripe Pricing Table script:', error)
    return false
  }
}

/**
 * Handle subscription to a plan
 * @param {string} priceId - Stripe price ID
 * @param {object} trpc - TRPC client
 * @returns {Promise<void>}
 */
export async function subscribeToPlan(priceId, trpc) {
  if (!priceId) {
    throw new Error('Price ID is required')
  }

  const returnUrl = `${window.location.origin}${window.location.pathname}`

  try {
    const data = await createCheckoutUrl(priceId, returnUrl, trpc)

    // Redirect to Stripe checkout
    if (data && data.url) {
      window.location.href = data.url
    }
    else {
      throw new Error('Invalid response from checkout service')
    }
  }
  catch (error) {
    console.error('Failed to create checkout session:', error)
    throw error
  }
}

/**
 * Open the Stripe customer portal
 * @param {object} trpc - TRPC client
 * @returns {Promise<void>}
 */
export async function openCustomerPortal(trpc) {
  const returnUrl = `${window.location.origin}${window.location.pathname}`

  try {
    const data = await createPortalUrl(returnUrl, trpc)

    // Redirect to Stripe portal
    if (data && data.url) {
      window.location.href = data.url
    }
    else {
      throw new Error('Invalid response from portal service')
    }
  }
  catch (error) {
    console.error('Failed to open customer portal:', error)
    throw error
  }
}

/**
 * Cancel a subscription
 * @param {object} trpc - TRPC client
 * @returns {Promise<object>} - Cancellation details
 */
export async function cancelSubscription(trpc) {
  try {
    return await trpc.stripe.cancelSubscription.mutate()
  }
  catch (error) {
    console.error('Failed to cancel subscription:', error)
    throw error
  }
}
